/**
 * The programme editor's toolbar: title, readiness, save status, and the
 * findings and file actions.
 *
 * @module components/editor/EditorBar
 */

import { Dropdown, Heading, Icon, Panel } from "@/components/ui";
import { useProgramme } from "@/hooks/useStore";
import { useOptionalEditorProgrammeSync } from "@/platform";
import type { ReadinessResult } from "@/server/types/readiness";
import { useEditorStore } from "@/state/store";

import { DevModeToggle, useDevMode } from "./DevModeToggle";
import { EditorPresence } from "./EditorPresence";
import { EditorReadiness } from "./EditorReadiness";
import { FindingsDrawer } from "./FindingsDrawer";
import { useProgrammeFileActions } from "./ProgrammeFileActions";
import { SaveStatus } from "./SaveStatus";

// ============================================================================
// Types
// ============================================================================

/** Props for Header component */
export interface EditorBarProps {
  /** Callback when navigation to a step is requested */
  onNavigateToStep: (stepKey: string, moduleId?: string) => void;
  /** The server's readiness for the programme, once it has been read. */
  readiness?: ReadinessResult | null;
  /** The last readiness read failed, so the rung may be out of date. */
  readinessFailed?: boolean;
  /** Reads the readiness again. */
  onRetryReadiness?: () => void;
  /** A readiness read asked for by hand is out. */
  readinessRetrying?: boolean;
  /** The programme is closed for editing, whatever this account may do. */
  readOnly?: boolean;
  /** Why it is closed, shown in place of the save state. */
  availability?: { short: string; full: string } | null;
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * Header component for the Programme Design Studio.
 * Displays programme title, readiness, and action buttons.
 */
export function EditorBar({
  onNavigateToStep,
  readiness = null,
  readinessFailed = false,
  onRetryReadiness = undefined,
  readinessRetrying = false,
  readOnly = false,
  availability = null,
}: EditorBarProps) {
  const { programme } = useProgramme();
  const backendSync = useOptionalEditorProgrammeSync();
  const isDevMode = useDevMode();
  const files = useProgrammeFileActions(programme, readOnly);

  const lastSaved = useEditorStore((store) => store.lastSaved);

  // Programme title display
  const displayTitle = programme.title?.trim() || "New Programme (Draft)";

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <Panel flush className="px-4 py-3" data-testid="header">
      {/* Title and actions share a row that never wraps from `sm` up, so a
          long title wraps inside its own cell and the actions keep the corner. */}
      <div className="flex items-start justify-between gap-x-4 gap-y-2 max-sm:flex-wrap">
        <div className="flex min-w-0 flex-1 flex-col gap-1 max-sm:basis-full">
          {/* An h1 rather than a span: the editor's surfaces open at h2, and
              without this the page had no heading of its own at all. */}
          <Heading level={1} size="lg" data-testid="programme-title">
            {displayTitle}
          </Heading>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <EditorReadiness
              readiness={readiness}
              failed={readinessFailed}
              onRetry={onRetryReadiness}
              retrying={readinessRetrying}
            />
            <SaveStatus lastSaved={lastSaved} backendSync={backendSync} readOnly={availability} />
          </div>
        </div>

        <div
          className="flex shrink-0 flex-wrap items-center gap-2 max-sm:ml-auto"
          role="toolbar"
          aria-label="Programme actions"
        >
          <EditorPresence
            showLabel="wide"
            moduleTitle={(moduleId) =>
              programme.modules?.find((module) => module.id === moduleId)?.title
            }
          />
          <FindingsDrawer onGoToStep={onNavigateToStep} />
          {isDevMode && <DevModeToggle mode={programme.mode} />}
          {/* JSON backup is read-only and import and reset are rare, so all
              three sit here: the menu stays on a closed programme. */}
          {files.items.length > 0 ? (
            <Dropdown
              aria-label="More programme actions"
              trigger={<Icon name="dots-three" weight="bold" size="xl" aria-hidden />}
              triggerAppearance="outline-square"
              triggerTestId="programme-more-actions"
              triggerTip="More programme actions"
              items={files.items}
            />
          ) : null}
          {files.fileInput}
        </div>
      </div>
    </Panel>
  );
}

export default EditorBar;
